export type FingerprintOptions = {
  slices?: number
  sliceMs?: number
  onProgress?: (view: 'A'|'B'|'C', done: number, total: number) => void
}

export type FingerprintResult = {
  uuid: string
  bits36: string
  rel: { L:number; M:number; R:number; S:number; T:number; mode:number }
  counts: {
    A: [number,number,number,number,number]
    B: [number,number,number,number,number]
    C: [number,number,number,number,number]
    totals: [number,number,number,number,number]
  }
  timingMs: number
}

/**
 * Compute a CPU-based device identifier in a Web Worker.
 * Returns a 36-bit relation string and a SHA-256→UUID of that string.
 */
export async function computeCpuFingerprint(opts: FingerprintOptions = {}): Promise<FingerprintResult>{
  const { slices=200, sliceMs=5, onProgress } = opts

  // Create worker using bundler-friendly URL pattern
  const workerUrl = new URL('./worker.js', import.meta.url)
  const w = new Worker(workerUrl, { type: 'module' })

  const t0 = performance.now()

  const bits36: string = await new Promise((resolve, reject)=>{
    w.onmessage = (ev: MessageEvent)=>{
      const msg = ev.data
      if(msg?.type === 'progress'){
        onProgress?.(msg.label, msg.done, msg.total)
      } else if (msg?.type === 'prelim'){
        resolve(msg.bits36 as string)
      }
    }
    w.onerror = (e)=> reject(e)
    w.postMessage({ slices, sliceMs })
  })

  w.terminate()

  const uuid = await bitsToUUID(bits36)

  // Note: to provide rel+counts we need a second channel or encode it inside bits36.
  // Simpler: run the worker a second time to fetch the metadata cheaply.
  const meta = await new Promise<any>((resolve, reject)=>{
    const w2 = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' })
    w2.onmessage = (ev: MessageEvent)=>{
      const msg = ev.data
      if (msg?.type === 'prelim'){
        resolve(msg)
      }
    }
    w2.onerror = (e)=> reject(e)
    w2.postMessage({ slices, sliceMs })
  })

  const dt = Math.max(1, Math.round(performance.now()-t0))

  const totals: [number,number,number,number,number] = [0,0,0,0,0]
  const A = meta.countsA as [number,number,number,number,number]
  const B = meta.countsB as [number,number,number,number,number]
  const C = meta.countsC as [number,number,number,number,number]
  for (let i=0;i<5;i++) totals[i] = A[i]+B[i]+C[i]

  return {
    uuid,
    bits36,
    rel: meta.rel,
    counts: { A, B, C, totals },
    timingMs: dt,
  }
}

export const hex = (u8: Uint8Array) => [...u8].map(b=>b.toString(16).padStart(2,'0')).join('')

export async function sha256hex(s: string): Promise<string>{
  const enc = new TextEncoder().encode(s)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  return hex(new Uint8Array(buf))
}

export function uuidFromHex32(h: string): string{
  const a = [...h]
  a[12] = '4'
  a[16] = ((parseInt(a[16],16)&0x3)|0x8).toString(16)
  return `${a.slice(0,8).join('')}-${a.slice(8,12).join('')}-${a.slice(12,16).join('')}-${a.slice(16,20).join('')}-${a.slice(20,32).join('')}`
}

export async function bitsToUUID(bits36: string): Promise<string>{
  const h = (await sha256hex(bits36)).slice(0,32)
  return uuidFromHex32(h)
}



